import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import TrustSignals from '../components/TrustSignals';
import * as FiIcons from 'react-icons/fi';

const {FiShield, FiLock, FiAlertTriangle, FiCheck, FiDollarSign, FiMonitor, FiWifi, FiDatabase} = FiIcons;

const CyberLiabilityPage = () => {
  const cyberThreats = [
    {
      title: "Data breaches",
      description: "Criminals can gain access to sensitive business or customer data.",
      icon: FiDatabase
    },
    {
      title: "Ransomware", 
      description: "Data is held hostage by bad actors demanding payment for its release.",
      icon: FiLock
    },
    {
      title: "Cyber attacks",
      description: "Computer viruses or denial of service attacks can take down computer systems and halt productivity.",
      icon: FiWifi
    },
    {
      title: "Wire fraud",
      description: "Funds transfer losses from a failure in your security or social engineering.",
      icon: FiDollarSign
    },
    {
      title: "Hardware failures",
      description: "Faulty hardware from malware or viruses can take down computers or networks.",
      icon: FiMonitor
    }
  ];

  const industries = [
    {
      name: "Financial services",
      description: "Social security numbers and personal customer account numbers and income information in your computer systems could make you vulnerable to digital criminal activity."
    },
    {
      name: "Legal services", 
      description: "Private client information and case details could be valuable assets for cyber criminals."
    },
    {
      name: "Real estate",
      description: "Funds in escrow and large digital transfers of cash could leave you and your clients at risk."
    },
    {
      name: "Retail and e-commerce",
      description: "Online stores or brick-and-mortar business owners are often targeted by hackers and cyber criminals."
    },
    {
      name: "Therapists and healthcare", 
      description: "Private health information needs to be kept private. Cyber security can help keep it that way."
    },
    {
      name: "Manufacturing",
      description: "Networked data systems that control workflow, monitor safety and track inventory could compromise production and human safety if tampered with by external actors."
    }
  ];

  const coverageAreas = [
    "Damaged computer replacement",
    "Phishing and data breach costs", 
    "Legal fees and forensics costs",
    "Customer notification expenses",
    "Credit monitoring services",
    "Regulatory penalties",
    "Ransomware negotiations",
    "Lost funds from wire fraud"
  ];

  const benefits = [
    "Free cyber security tools for monitoring and protection",
    "24/7 expert response after a cyber attack",
    "Protection for physical hardware damaged by viruses", 
    "Financial help after phishing, data breaches, and ransomware",
    "Recovery funds for wire fraud losses"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{backgroundImage: 'url(https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)'}}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8}}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Cyber Liability Insurance: Protect Your Business from Digital Threats
            </motion.h1>
            <motion.p
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.2}}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Wire fraud, social engineering and other cybercrime can unplug a small business. Cyber liability can help protect you from digital disasters.
            </motion.p>
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.4}}
            >
              <CTAButton text="Get Your Cyber Insurance Quote" size="xl" className="mb-8" />
            </motion.div>
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{duration: 0.8, delay: 0.6}}
            >
              <TrustSignals className="text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cyber Threats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Cyber Insurance for Small Business Can Help Protect You From:
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cyberThreats.slice(0, 3).map((threat, index) => (
              <motion.div
                key={threat.title}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="text-center p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={threat.icon} className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{threat.title}</h3>
                <p className="text-gray-600">{threat.description}</p>
              </motion.div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
            {cyberThreats.slice(3).map((threat, index) => (
              <motion.div
                key={threat.title}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: (index + 3) * 0.1}}
                className="text-center p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={threat.icon} className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{threat.title}</h3>
                <p className="text-gray-600">{threat.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.8}}
            className="text-center mt-12"
          >
            <CTAButton text="Get Instant Quote" />
          </motion.div>
        </div>
      </section>

      {/* What is Cyber Insurance */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Cyber Insurance?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Cyber insurance can help small businesses prevent, assess and quickly respond to many technology-based problems, including:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Compromised IT infrastructure</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Systems failure events</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Cyber attacks</span>
                </li>
                <li className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Data breaches</span>
                </li>
              </ul>
              <p className="text-gray-700">
                Prevention is key, but fast response time in the event of an incident can help mitigate damage and loss to your business.
              </p>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cyber Liability Insurance Can Provide:</h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiShield} className="h-5 w-5 text-primary-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coverage Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Does Cyber Insurance Cover?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Even with vigilant monitoring, staff training and best practices in place, a cyber attack can slow or stop your small business.
            </p>
          </div>
          
          {/* Coverage Areas Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {coverageAreas.map((area, index) => (
              <motion.div
                key={area}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-gray-50 p-6 rounded-lg text-center"
              >
                <SafeIcon icon={FiShield} className="h-8 w-8 text-primary-600 mx-auto mb-3" />
                <span className="text-gray-700 font-medium">{area}</span>
              </motion.div>
            ))}
          </div>

          {/* Detailed Coverage Sections */}
          <div className="space-y-12">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="bg-blue-50 p-8 rounded-lg border-l-4 border-blue-400"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Phishing and Data Breach Costs</h3>
              <p className="text-gray-700 mb-4">
                Insurance can help cover the costs if a hacker gains access to an employee's email or breaches your network. This could include expenses such as:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <li className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-700">Legal fees</span>
                </li>
                <li className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-700">Forensics and PR costs</span>
                </li>
                <li className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-700">Customer notifications</span>
                </li>
                <li className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-700">Credit monitoring</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="bg-red-50 p-8 rounded-lg border-l-4 border-red-400"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ransomware Protection</h3>
              <p className="text-gray-700 mb-4">
                A ransomware attack occurs when data is held hostage by bad actors demanding payment for its release. It can mean a total interruption of your company's computer systems, permanent loss of data, or reputational damage to your brand.
              </p>
              <p className="text-gray-700">
                In the event your organization does pay a ransom, a trained response team can help negotiate a significant reduction in the demand.
              </p>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.4}}
              className="bg-green-50 p-8 rounded-lg border-l-4 border-green-400"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Wire Fraud Protection</h3>
              <p className="text-gray-700">
                Imagine a funds request sent to accounting from a trusted employee or vendor. The funds are transferred — and only later it's discovered that the request was not authentic. Cyber insurance can help monitor your computer systems to prevent fraud and also help you recover if you suffer a loss.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Who Needs Cyber Security Insurance?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Any small business that handles digital financial transactions and sensitive data could be targeted by cyber criminals.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">{industry.name}</h3>
                <p className="text-gray-600">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What's the Benefit of Cyber Insurance for Small Businesses?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiShield} className="h-8 w-8 text-primary-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Comprehensive Protection</h3>
                    <p className="text-gray-700">Coverage for financial loss and damage up to $15 million with monitoring tools to prevent incidents.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiAlertTriangle} className="h-8 w-8 text-secondary-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">24/7 Expert Response</h3>
                    <p className="text-gray-700">Immediate support available around the clock to help you quickly respond to cyber incidents.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiLock} className="h-8 w-8 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Privacy Protection</h3>
                    <p className="text-gray-700">Protection from privacy violations and coverage for decisions made by company executives during incidents.</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Comprehensive Coverage Available</h3>
              <p className="text-lg text-gray-700 mb-6">
                Cyber liability insurance is available through Next Insurance, providing comprehensive digital protection for your business.
              </p>
              <CTAButton text="Start Your Free Quote Today" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Cyber Insurance FAQ
            </h2>
          </div>
          <div className="space-y-8">
            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="bg-white p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                How much does cyber insurance cost?
              </h3>
              <p className="text-gray-700">
                Cyber insurance can be very affordable. The price depends on your company's network security, the type of sensitive data you collect, your industry, business operations, and coverage limits you choose.
              </p>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="bg-white p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Does cyber insurance cover ransomware payments?
              </h3>
              <p className="text-gray-700">
                Yes, in the event a hacker encrypts your business data and demands payment, cyber liability insurance can help cover the cost to restore your data and may include expert negotiation services to reduce ransom demands.
              </p>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.4}}
              className="bg-white p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Is cyber security insurance tax deductible?
              </h3>
              <p className="text-gray-700">
                Cyber liability insurance premiums can typically be deducted from your taxes as an "ordinary and necessary" business expense. Consult with a licensed accounting professional to ensure you qualify.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Don't Let Cyber Criminals Shut Down Your Business
          </motion.h2>
          <motion.p
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="text-xl mb-8"
          >
            Protect your digital assets with comprehensive cyber liability insurance. Get your free quote today.
          </motion.p>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.4}}
          >
            <CTAButton 
              text="Start Your Free Instant Quote Today" 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-100" 
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CyberLiabilityPage;